import { aadFundUrl, donatePaymentUrl, ticketMarketplacePath } from './siteMenu'

export type PageBlock =
  | { kind: 'text'; id?: string; heading?: string; paragraphs: string[] }
  | { kind: 'cards'; id?: string; heading: string; intro?: string; cards: { title: string; body: string }[] }
  | { kind: 'list'; id?: string; heading: string; intro?: string; items: string[] }
  | {
      kind: 'links'
      id?: string
      heading: string
      intro?: string
      links: { label: string; description: string; href: string }[]
    }

export type PageAction = {
  label: string
  href: string
  external?: boolean
  variant?: 'primary' | 'outline'
}

export type SitePage = {
  path: string
  eyebrow: string
  title: string
  lede: string
  image?: string
  imageAlt?: string
  blocks: PageBlock[]
  cta?: {
    heading: string
    body: string
    actions: PageAction[]
  }
}

const projectDirectory = {
  kind: 'links' as const,
  heading: 'Other AAN projects',
  links: [
    {
      label: 'AAN Productions',
      description: 'Film, television and stage work produced and co-produced by the network.',
      href: '/projects/aan-productions',
    },
    {
      label: 'AAN Events',
      description: 'Screenings, premieres, festivals and live performances you can book today.',
      href: ticketMarketplacePath,
    },
    {
      label: 'AAN EDUC8',
      description: 'Training, masterclasses and mentorship for the next generation of creatives.',
      href: '/projects/aan-educ8',
    },
    {
      label: 'TalentAfrica.net',
      description: 'A directory that connects African talent with the people casting and hiring.',
      href: '/projects/talent-africa',
    },
    {
      label: 'Adinkra Experience',
      description: 'An online marketplace for African craft, design and cultural products.',
      href: '/projects/adinkra-experience',
    },
    {
      label: 'Sankofa Episode',
      description: 'Documentary storytelling that returns to African history and heritage.',
      href: '/projects/sankofa-episode',
    },
    {
      label: 'AECinemax',
      description: 'Cinema distribution and exhibition built for African audiences.',
      href: '/projects/aecinemax',
    },
    {
      label: 'AAD Fund',
      description: 'The Africa Arts Development Fund — grants and support for artists.',
      href: '/projects/aad-fund',
    },
  ],
}

export const sitePages: SitePage[] = [
  {
    path: '/about',
    eyebrow: 'About AAN',
    title: 'African Art Network',
    lede:
      'African Art Network is a movement dedicated to supporting and contributing to a vibrant creative economy by promoting the African experience and all forms of artistic and cultural expression through various channels and platforms.',
    image: '/assets/gallery-web-1.jpg',
    imageAlt: 'African Art Network productions and audiences',
    blocks: [
      {
        kind: 'text',
        heading: 'Who we are',
        paragraphs: [
          'African Art Network (AAN) is a company limited by guarantee working across film, theatre, music, visual arts and cultural heritage. We build the platforms, programmes and partnerships that allow African creatives to make work, reach audiences and earn a living from what they do.',
          'Our work runs from production and distribution through to training, talent development and audience building — each of our initiatives addresses a different gap in the African creative value chain.',
        ],
      },
      {
        kind: 'text',
        id: 'vision',
        heading: 'Vision & mission',
        paragraphs: [
          'Vision: African Art Network aims to be the leading catalyst for the promotion of African arts and culture, foreseeing a thriving environment where artists can express their creativity, connect with audiences, and inspire the world.',
          'Mission: To promote the African experience through artistic and cultural expression — creating opportunity, visibility and sustainable income for artists across the continent and the diaspora.',
        ],
      },
      {
        kind: 'cards',
        heading: 'What we stand for',
        cards: [
          {
            title: 'Authentic African storytelling',
            body: 'Stories told by Africans, on African terms, with the depth and specificity our histories deserve.',
          },
          {
            title: 'Opportunity for creatives',
            body: 'Practical routes into the industry: training, castings, commissions, funding and paid work.',
          },
          {
            title: 'Access for audiences',
            body: 'Affordable, well-run screenings, performances and exhibitions across cities and communities.',
          },
          {
            title: 'A sustainable creative economy',
            body: 'Building the infrastructure — venues, platforms, funding, distribution — that makes the work last.',
          },
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Work with us',
      body: 'Partner with the network, volunteer your skills, or join as a member and help shape what we build next.',
      actions: [
        { label: 'Join AAN', href: '/join' },
        { label: 'Contact us', href: '/contact', variant: 'outline' },
      ],
    },
  },
  {
    path: '/about/team',
    eyebrow: 'About AAN',
    title: 'AAN Team',
    lede:
      'The African Art Network team brings together producers, educators, technologists and cultural organisers working across Ghana and the wider continent.',
    blocks: [
      {
        kind: 'text',
        paragraphs: [
          'Our team is deliberately cross-disciplinary. Production and creative leads develop and deliver our film, theatre and television work; programme leads run AAN EDUC8 and our talent initiatives; operations, technology and partnership leads keep the platforms, events and funding running.',
          'Alongside the core team, AAN works with a standing network of directors, cinematographers, designers, performers, technicians and cultural practitioners who are brought onto projects as they are commissioned.',
        ],
      },
      {
        kind: 'cards',
        heading: 'How the team is organised',
        cards: [
          {
            title: 'Production & creative',
            body: 'Development, producing and delivery for AAN Productions, Sankofa Episode and commissioned work.',
          },
          {
            title: 'Programmes & education',
            body: 'AAN EDUC8 curriculum, masterclasses, mentorship and the schools and community programmes.',
          },
          {
            title: 'Talent & partnerships',
            body: 'TalentAfrica.net, casting calls, and relationships with partners, sponsors and institutions.',
          },
          {
            title: 'Operations & platforms',
            body: 'Events, ticketing, the Adinkra Experience marketplace and AECinemax distribution.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'Join the team',
        paragraphs: [
          'We recruit for productions, programmes and platform roles throughout the year, and we work with volunteers on events and community projects. If you would like to be considered, send your profile and portfolio to the team.',
        ],
      },
    ],
    cta: {
      heading: 'Get in touch with the team',
      body: 'Send us your profile, a project proposal, or a question about our work.',
      actions: [
        { label: 'Contact us', href: '/contact' },
        { label: 'Volunteer with AAN', href: '/join/volunteers', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects',
    eyebrow: 'Projects & Initiatives',
    title: 'Projects & Initiatives',
    lede:
      'Each AAN initiative tackles a different part of the African creative value chain — from making the work, to training the people who make it, to getting it in front of audiences.',
    blocks: [
      { ...projectDirectory, heading: 'Explore our initiatives' },
    ],
    cta: {
      heading: 'Support the work',
      body: 'Our initiatives are sustained by partners, members and donors who believe in African creative work.',
      actions: [
        { label: 'Donate', href: '/donate' },
        { label: 'Become a partner', href: '/join/partners', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/aan-productions',
    eyebrow: 'Projects & Initiatives',
    title: 'AAN Productions',
    lede:
      'Experience African film and theatre excellence. AAN Productions develops, produces and co-produces original African stories for stage and screen.',
    image: '/assets/event-black-star.jpg',
    imageAlt: 'The Black Star stage play',
    blocks: [
      {
        kind: 'text',
        heading: 'What we produce',
        paragraphs: [
          'AAN Productions is the production arm of African Art Network. We develop original screenplays and stage plays, co-produce with independent filmmakers and theatre companies, and provide production support to creatives who have the story but not yet the infrastructure.',
          'Our productions are staged and screened through AAN Events and distributed through AECinemax, so work made within the network has a clear route to an audience.',
        ],
      },
      {
        kind: 'list',
        heading: 'What the production arm offers',
        items: [
          'Development support: script editing, dramaturgy and project packaging',
          'Full production and co-production for film, television and stage',
          'Crew, cast and technical support drawn from the AAN talent network',
          'Rehearsal, shoot and staging logistics',
          'Distribution and exhibition through AECinemax and AAN Events',
        ],
      },
      {
        kind: 'text',
        heading: 'Work with AAN Productions',
        paragraphs: [
          'We accept project submissions from writers, directors and producers working on African stories. Send a synopsis, a treatment or a script along with a short note about where the project currently stands.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'See what is on now',
      body: 'Our current productions, screenings and stage runs are listed on the AAN Events marketplace.',
      actions: [
        { label: 'Browse events', href: ticketMarketplacePath },
        { label: 'Submit a project', href: '/contact', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/aan-educ8',
    eyebrow: 'Projects & Initiatives',
    title: 'AAN EDUC8',
    lede:
      'Empowering the future of arts. AAN EDUC8 is our training and capacity-building programme for young African creatives.',
    image: '/assets/STORYTELLING.jpg',
    imageAlt: 'AAN EDUC8 storytelling masterclass',
    blocks: [
      {
        kind: 'text',
        heading: 'The programme',
        paragraphs: [
          'AAN EDUC8 exists because talent across the continent is abundant while formal training is not. The programme delivers practical, industry-led instruction in the crafts that make up film, theatre and the wider creative economy.',
          'Sessions are led by working practitioners — directors, writers, cinematographers, designers and producers — and are built around doing the work rather than only studying it.',
        ],
      },
      {
        kind: 'cards',
        heading: 'What we run',
        cards: [
          {
            title: 'Masterclasses',
            body: 'Intensive sessions with established practitioners on storytelling, directing, performance and production craft.',
          },
          {
            title: 'Workshops & short courses',
            body: 'Hands-on training in screenwriting, camera, sound, lighting, design and stage technique.',
          },
          {
            title: 'Mentorship',
            body: 'Emerging creatives paired with experienced professionals over the life of a project.',
          },
          {
            title: 'Schools & community outreach',
            body: 'Arts education taken into schools and community spaces to reach young people early.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'Who it is for',
        paragraphs: [
          'Emerging filmmakers, performers, technicians, writers and arts administrators — whether you are starting out or already working and looking to sharpen a specific craft. Upcoming masterclasses and courses are published on the events page as they open for booking.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Join the next session',
      body: 'Masterclasses and workshops are announced and booked through AAN Events.',
      actions: [
        { label: 'See upcoming sessions', href: ticketMarketplacePath },
        { label: 'Ask about EDUC8', href: '/contact', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/talent-africa',
    eyebrow: 'Projects & Initiatives',
    title: 'TalentAfrica.net',
    lede:
      'Explore amazing African talent. TalentAfrica.net is the network directory that connects African creatives with the people casting, commissioning and hiring.',
    image: '/assets/event-call-actors.jpg',
    imageAlt: 'AAN call for actors',
    blocks: [
      {
        kind: 'text',
        heading: 'The idea',
        paragraphs: [
          'Casting directors and producers routinely say they cannot find the talent. Talent routinely says it cannot find the work. TalentAfrica.net closes that gap with a searchable directory of African performers, crew and creative professionals.',
          'Profiles carry credits, showreels, portfolios and availability, so a producer can go from search to shortlist without leaving the platform.',
        ],
      },
      {
        kind: 'cards',
        heading: 'For talent and for producers',
        cards: [
          {
            title: 'Build a profile',
            body: 'Actors, dancers, musicians, crew and technicians publish credits, reels, photos and availability.',
          },
          {
            title: 'Get discovered',
            body: 'Be visible to producers, casting directors and brands working across the continent.',
          },
          {
            title: 'Open casting calls',
            body: 'Auditions and crew calls from AAN Productions and partner productions are posted to the network.',
          },
          {
            title: 'Search and shortlist',
            body: 'Producers filter by discipline, experience, location and availability to build a cast or crew.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'Register your talent',
        paragraphs: [
          'Registration is open to creatives across Africa and the diaspora. Send your discipline, credits and portfolio to the team and we will set up your listing.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Get listed',
      body: 'Send your profile to the team, or watch the events page for open auditions and crew calls.',
      actions: [
        { label: 'Register your profile', href: '/contact' },
        { label: 'See open calls', href: ticketMarketplacePath, variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/adinkra-experience',
    eyebrow: 'Projects & Initiatives',
    title: 'Adinkra Experience',
    lede:
      'Your online African marketplace. Adinkra Experience brings African craft, design and cultural products to buyers at home and abroad.',
    image: '/assets/adinkra-experience.jpg',
    imageAlt: 'Adinkra Experience — your online African marketplace',
    blocks: [
      {
        kind: 'text',
        heading: 'A marketplace for African makers',
        paragraphs: [
          'Adinkra Experience is an online marketplace built for African artisans, designers and cultural producers. Textiles, adinkra prints, beadwork, sculpture, fashion and craft are listed by the makers themselves and sold directly to buyers.',
          'The platform handles the storefront, discovery and payment so makers can concentrate on the work — and so buyers know what they are buying and who made it.',
        ],
      },
      {
        kind: 'list',
        heading: 'What you will find',
        items: [
          'Textiles, kente and adinkra printed cloth',
          'Fashion, accessories and jewellery from African designers',
          'Sculpture, carving, beadwork and visual art',
          'Home and craft pieces made by artisan collectives',
          'Cultural products tied to AAN productions and events',
        ],
      },
      {
        kind: 'text',
        heading: 'Sell on Adinkra Experience',
        paragraphs: [
          'Artisans, designers and collectives can apply to list on the marketplace. Get in touch with a short description of your work, your production capacity and where you are based.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Become a seller',
      body: 'Tell us about your work and we will get you set up on the marketplace.',
      actions: [
        { label: 'Apply to sell', href: '/contact' },
        { label: 'See our other projects', href: '/projects', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/sankofa-episode',
    eyebrow: 'Projects & Initiatives',
    title: 'Sankofa Episode',
    lede:
      'Sankofa — go back and get it. A documentary and storytelling series returning to African history, heritage and the people who carry it.',
    image: '/assets/gallery-web-7.jpg',
    imageAlt: 'Sankofa Episode heritage storytelling',
    blocks: [
      {
        kind: 'text',
        heading: 'The series',
        paragraphs: [
          'Sankofa Episode takes its name from the Akan principle that there is no shame in returning to fetch what has been left behind. Each episode goes back to a piece of African history, tradition or cultural practice and brings it forward for a contemporary audience.',
          'Episodes combine documentary footage, oral history from elders and practitioners, archival material and dramatised sequences produced with AAN Productions.',
        ],
      },
      {
        kind: 'list',
        heading: 'What the series covers',
        items: [
          'Oral histories recorded with elders, custodians and practitioners',
          'Traditional crafts, symbols and their meanings — including adinkra',
          'Festivals, rites and the communities that sustain them',
          'Historical figures and events under-told in mainstream accounts',
          'Contemporary artists working from traditional forms',
        ],
      },
      {
        kind: 'text',
        heading: 'Contribute to an episode',
        paragraphs: [
          'We are always looking for stories, custodians, archives and locations. If you hold knowledge or material that belongs in the series, we would like to hear from you.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Suggest a story',
      body: 'Share a subject, a community or an archive you believe the series should document.',
      actions: [
        { label: 'Contact the team', href: '/contact' },
        { label: 'Watch for screenings', href: ticketMarketplacePath, variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/aecinemax',
    eyebrow: 'Projects & Initiatives',
    title: 'AECinemax',
    lede:
      'Cinema built for African audiences. AECinemax is our exhibition and distribution initiative, putting African films on African screens.',
    image: '/assets/aecinemax.jpg',
    imageAlt: 'AECinemax',
    blocks: [
      {
        kind: 'text',
        heading: 'Why AECinemax',
        paragraphs: [
          'African films are made every year that never reach a screen at home. Cinema infrastructure is concentrated in a handful of cities, and distribution terms rarely favour independent African producers.',
          'AECinemax addresses both: a distribution route for African film and a programme of screenings that takes cinema to audiences where the screens are not — community venues, campuses and pop-up cinemas alongside conventional theatres.',
        ],
      },
      {
        kind: 'cards',
        heading: 'What we do',
        cards: [
          {
            title: 'Exhibition',
            body: 'Screenings, premieres and film festival programmes across cities and community venues.',
          },
          {
            title: 'Distribution',
            body: 'Getting independent African films in front of paying audiences on fair terms.',
          },
          {
            title: 'Curated programming',
            body: 'Seasons and retrospectives built around African cinema, past and present.',
          },
          {
            title: 'Ticketing',
            body: 'Every screening is bookable online through the AAN Events platform.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'Submit a film',
        paragraphs: [
          'Filmmakers with a completed feature, documentary or short can submit for consideration. Send a synopsis, a link to the film or trailer, and details of the rights you hold.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Book a screening',
      body: 'Current and upcoming screenings are listed on the AAN Events marketplace.',
      actions: [
        { label: 'See screenings', href: ticketMarketplacePath },
        { label: 'Submit your film', href: '/contact', variant: 'outline' },
      ],
    },
  },
  {
    path: '/projects/aad-fund',
    eyebrow: 'Projects & Initiatives',
    title: 'AAD Fund',
    lede:
      'The Africa Arts Development Fund supports artists, cultural workers and creative institutions across the continent through funding, opportunities and development programmes.',
    image: '/assets/gallery-web-2.jpg',
    imageAlt: 'Africa Arts Development Fund',
    blocks: [
      {
        kind: 'text',
        heading: 'What the fund does',
        paragraphs: [
          'The Africa Arts Development Fund (AADF) exists to put resources directly behind African creative work. Funding goes to artists making work, to the programmes that train them, and to the institutions and platforms that keep the sector standing.',
          'Contributions sustain creative education, artist support, cultural exchange and the platforms that connect African talent with wider audiences.',
        ],
      },
      {
        kind: 'list',
        heading: 'Where the funding goes',
        items: [
          'Direct support for artists and productions',
          'AAN EDUC8 training, masterclasses and mentorship',
          'Community and schools arts programmes',
          'Cultural exchange and touring opportunities',
          'Platforms and infrastructure that keep African work visible',
        ],
      },
      {
        kind: 'text',
        heading: 'Support the fund',
        paragraphs: [
          'Individual donations, corporate partnerships and institutional grants all sustain the fund. Donations are processed securely through Paystack, and partners can contact the team to discuss structured support.',
        ],
      },
      projectDirectory,
    ],
    cta: {
      heading: 'Donate to the AAD Fund',
      body: 'Your contribution supports the future of arts in Africa.',
      actions: [
        { label: 'Donate now', href: donatePaymentUrl, external: true },
        { label: 'Read more about AADF', href: aadFundUrl, external: true, variant: 'outline' },
      ],
    },
  },
  {
    path: '/join',
    eyebrow: 'Join AAN',
    title: 'Join AAN',
    lede:
      'African Art Network is a movement, and it runs on the people in it. There are four ways to be part of the work.',
    blocks: [
      {
        kind: 'links',
        heading: 'Ways to join',
        links: [
          {
            label: 'Partners',
            description: 'Organisations, brands and institutions collaborating with AAN to transform the arts.',
            href: '/join/partners',
          },
          {
            label: 'Volunteers',
            description: 'Give your time and skills to productions, events and community programmes.',
            href: '/join/volunteers',
          },
          {
            label: 'Members',
            description: 'Join the AAN community and get closer to the work, the people and the opportunities.',
            href: '/join/members',
          },
          {
            label: 'Donate',
            description: 'Support the future of arts in Africa through the Africa Arts Development Fund.',
            href: '/donate',
          },
        ],
      },
    ],
    cta: {
      heading: 'Not sure where you fit?',
      body: 'Tell us what you do and what you are looking for, and we will point you to the right programme.',
      actions: [
        { label: 'Contact us', href: '/contact' },
        { label: 'See our projects', href: '/projects', variant: 'outline' },
      ],
    },
  },
  {
    path: '/join/partners',
    eyebrow: 'Join AAN',
    title: 'Partners',
    lede:
      'Collaborate to transform the arts. AAN partners with organisations, institutions and brands that want to invest in African creative work.',
    blocks: [
      {
        kind: 'text',
        heading: 'Why partner with AAN',
        paragraphs: [
          'Partnership with African Art Network puts your organisation alongside authentic African creative work and the audiences it reaches — across film, theatre, education and cultural heritage.',
          'We work with partners on terms that suit the collaboration: sponsorship of a production or programme, co-delivery of an initiative, venue and technical partnerships, or long-term institutional support.',
        ],
      },
      {
        kind: 'cards',
        heading: 'Ways to partner',
        cards: [
          {
            title: 'Production & event sponsorship',
            body: 'Back a film, stage production, festival or screening season and share in its visibility.',
          },
          {
            title: 'Programme partnership',
            body: 'Co-deliver AAN EDUC8 training, community outreach or talent development with us.',
          },
          {
            title: 'Venue & technical partnership',
            body: 'Provide space, equipment or services that make productions and screenings possible.',
          },
          {
            title: 'Institutional support',
            body: 'Cultural bodies, foundations and development partners funding the sector at scale.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'Start a conversation',
        paragraphs: [
          'Send us a note about your organisation and what you would like to achieve. We will come back with a proposal for how a partnership could work.',
        ],
      },
    ],
    cta: {
      heading: 'Become a partner',
      body: 'Tell us about your organisation and the kind of collaboration you have in mind.',
      actions: [
        { label: 'Contact the partnerships team', href: '/contact' },
        { label: 'Support the AAD Fund', href: '/projects/aad-fund', variant: 'outline' },
      ],
    },
  },
  {
    path: '/join/volunteers',
    eyebrow: 'Join AAN',
    title: 'Volunteers',
    lede:
      'Make a difference with AAN. Volunteers keep our productions, events and community programmes running — and get real experience in return.',
    blocks: [
      {
        kind: 'text',
        heading: 'What volunteering involves',
        paragraphs: [
          'AAN volunteers work on live productions and events: front of house, ushering, box office and ticket scanning, stage and set support, production runners, photography and social media, and community programme facilitation.',
          'It is genuine industry exposure. Volunteers work alongside professional crews, and many go on to paid roles on later productions or into AAN EDUC8 training.',
        ],
      },
      {
        kind: 'list',
        heading: 'Where we need help',
        items: [
          'Events: front of house, ushering, box office and ticket scanning',
          'Productions: runners, stage crew, set and props support',
          'Media: photography, videography and social media coverage',
          'Community programmes: workshop and outreach facilitation',
          'Administration: registration, logistics and coordination',
        ],
      },
      {
        kind: 'text',
        heading: 'Who can volunteer',
        paragraphs: [
          'Anyone with an interest in the arts and a willingness to work — students, emerging creatives and professionals giving time. No prior experience is required for most roles; we will brief and train you before each production or event.',
        ],
      },
    ],
    cta: {
      heading: 'Sign up to volunteer',
      body: 'Send us your name, your city, what you are interested in and when you are available.',
      actions: [
        { label: 'Volunteer with AAN', href: '/contact' },
        { label: 'See upcoming events', href: ticketMarketplacePath, variant: 'outline' },
      ],
    },
  },
  {
    path: '/join/members',
    eyebrow: 'Join AAN',
    title: 'Members',
    lede:
      'Join the AAN community. Membership connects you to the artists, productions and opportunities across the network.',
    blocks: [
      {
        kind: 'text',
        heading: 'The AAN community',
        paragraphs: [
          'Members are the standing community around African Art Network: artists, performers, crew, arts administrators, educators and audiences who want to be close to the work rather than only watch it from outside.',
          'Membership is open to creatives and supporters across Africa and the diaspora.',
        ],
      },
      {
        kind: 'cards',
        heading: 'What members get',
        cards: [
          {
            title: 'First access',
            body: 'Early notice of productions, screenings, masterclasses and open casting calls.',
          },
          {
            title: 'Opportunities',
            body: 'Auditions, crew calls, commissions and training places circulated to members first.',
          },
          {
            title: 'Community',
            body: 'A network of practitioners to collaborate with, learn from and build projects alongside.',
          },
          {
            title: 'A voice in the movement',
            body: 'Members help shape the programmes AAN runs and the work the network takes on.',
          },
        ],
      },
      {
        kind: 'text',
        heading: 'How to join',
        paragraphs: [
          'Get in touch with your name, your discipline or interest, and where you are based, and the team will register you with the community.',
        ],
      },
    ],
    cta: {
      heading: 'Become a member',
      body: 'Join the community and be first to hear about everything the network is doing.',
      actions: [
        { label: 'Join the community', href: '/contact' },
        { label: 'Explore our projects', href: '/projects', variant: 'outline' },
      ],
    },
  },
]

export const sitePageByPath = new Map(sitePages.map((page) => [page.path, page]))
